import { useEffect, useState } from 'react';
import { type useRouter, withRouter } from 'next/router';
import { compose } from 'redux';
import { connect, type ConnectedProps } from 'react-redux';
import Link from 'next/link';
import { EmailVerification } from '@/components/app/authForms/emailVerification';
import {
  StyledAuthPageContent,
  StyledAuthPageLayout,
} from '@/components/app/authForms/layout/styles';
import { SignUpFormContainer } from '@/components/app/authForms/signUpForm';
import Layout from '@/components/app/layout/index';
import StringAccessor from '@/components/cmsConfig/stringAccessor';
import { StyledHr } from '@/components/uielements/hr';
import { ROUTES } from '@/constants/routes';
import authActions from '@/redux/auth/actions';
import { type RootState } from '@/types/app';
import { userLoggedIn } from '@/utils/auth';
import { deriveReferrer } from '@/utils/global';
import { safelyGetConfig } from '@/utils/stringHelpers';

interface Props extends ReduxProps {
  router: ReturnType<typeof useRouter>;
}

const SignUp = ({
  router,
  webConfig,
  emailVerificationToggle,
  loading,
  toggleEmailVerification,
}: Props) => {
  const loginReferrer = deriveReferrer(router);

  const [signedInRedirect, setSignedInRedirect] = useState(false);

  useEffect(() => {
    if (emailVerificationToggle) {
      toggleEmailVerification(false);
    }
  }, []);

  /**
   * Guard the account-creation form against already-signed-in diners (TEAM-13451).
   * Uses userLoggedIn() — the synchronous persisted-token check the sibling auth pages use —
   * NOT the async Redux `me` state.
   *
   * Both the redirect and the "hide the form" flip happen AFTER mount, via state, so the first
   * client render still matches the server-rendered form and hydration stays clean. Deciding
   * this during render instead (router.push in the render body, or returning null on the first
   * client render while the server rendered the form) strands the SSR form DOM on the /account
   * URL mid-transition — a signed-in diner hard-loading /sign-up gets stuck showing the form.
   */
  useEffect(() => {
    if (userLoggedIn()) {
      setSignedInRedirect(true);
      void router.push(ROUTES.ACCOUNT);
    }
  }, []);

  if (signedInRedirect) {
    return null;
  }

  return (
    <Layout
      $backgroundOverride={true}
      disabled={!!safelyGetConfig(webConfig, 'accounts.disable_accounts')}
    >
      <StyledAuthPageLayout>
        <StyledAuthPageContent>
          {!loading && emailVerificationToggle ? (
            <EmailVerification referrer={loginReferrer} />
          ) : (
            <>
              <StringAccessor tag="h1" accessor="signup.header" html={true} />

              <StringAccessor tag="h3" accessor="signup.subheader" html={true} />

              <StyledHr style={{ marginBottom: '10px' }} $background="transparent" />

              <SignUpFormContainer />

              <StyledHr />
              <p>
                <StringAccessor accessor="login.prompt" html={true} />
                &nbsp;
                <Link href={`${ROUTES.LOGIN}?referrer=${loginReferrer}`}>
                  <StringAccessor accessor="login.cta" />
                </Link>
              </p>
            </>
          )}
        </StyledAuthPageContent>
      </StyledAuthPageLayout>
    </Layout>
  );
};

const mapStateToProps = (state: RootState) => ({
  emailVerificationToggle: state.app.auth.emailVerificationToggle,
  loading: state.app.auth.loading,
  webConfig: state.app.cmsConfig.webConfig,
});

const mapDispatchToProps = {
  toggleEmailVerification: authActions.toggleEmailVerification,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default compose(connector, withRouter)(SignUp);
